import React, { useEffect, useState } from 'react'

import moment from 'moment'

import {
  Box,
  Card,
  Flex,
  Header,
  Icon,
  Link,
  styled,
  Table,
  TableColumn,
  Tag,
  TagProps,
  Text
} from '@driftt/dds'
import { faEye } from '@driftt/dds.icons/light/faEye'
import { faMagnifyingGlass } from '@driftt/dds.icons/light/faMagnifyingGlass'

import { fetchAccountEngagementSummary } from 'api/AccountActivityAPI'
import { segmentTrack, TrackType } from 'utils/tracking'

export interface AccountSiteVisitInfoTableProps {
  accountId: string
}

export interface AccountSiteVisitInfo {
  pageUrl: string
  numberOfVisits: number
}

const tableColumns: TableColumn<AccountSiteVisitInfo>[] = [
  {
    value: 'pageUrl',
    header: 'PAGE',
    size: 220,
    disableSort: true,
    cell: ({ pageUrl }) => {
      const url = new URL(pageUrl)
      return (
        <Link
          css={{ maxWidth: '190px' }}
          truncate
          variant="inline"
          external={true}
          href={pageUrl}
          onClick={() => segmentTrack(TrackType.ACCOUNT_SITE_VISIT_INFO_LINK_CLICKED, pageUrl)}>{`${
          url.pathname
        }${getUrlFragment(pageUrl)}`}</Link>
      )
    }
  },
  {
    id: 'visits',
    header: 'VISITS',
    size: 70,
    disableSort: true,
    value: 'numberOfVisits',
    align: 'right'
  }
]

const HeaderIcon = styled(Icon, {
  color: 'white',
  padding: '3px',
  borderRadius: '3px',
  fontSize: '$2',
  marginRight: '4px'
})

const getUrlFragment = (url: string): string => {
  const fragmentLocation = url.indexOf('#')
  let fragment = fragmentLocation > 0 ? `${url.substring(fragmentLocation)}` : ''
  if (fragment.indexOf('?') > 0) fragment = fragment.substring(0, fragment.indexOf('?'))

  return fragment.length > 1 ? fragment : ''
}

const getRandomVariant = (index: number): TagProps['variant'] => {
  const tagColors = ['indigo', 'teal', 'yellow', 'pink', 'green', 'blue', 'gray', 'purple'] as any
  return index >= tagColors.length ? tagColors[Math.floor(Math.random() * 8)] : tagColors[index]
}

const getTopicMessage = (topicsSize: number): string => {
  if (topicsSize === 1) {
    return `Researching 1 Topic`
  }

  if (topicsSize >= 10) {
    return `Researching 10+ Topics`
  }

  return `Researching ${topicsSize} Topics`
}

const AccountSiteVisitInfoTable = ({ accountId }: AccountSiteVisitInfoTableProps) => {
  const [accountTopTopics, setAccountTopTopics] = useState<string[]>([])
  const [accountSiteVisitInfo, setAccountSiteVisitInfo] = useState<AccountSiteVisitInfo[]>([])
  useEffect(() => {
    fetchAccountEngagementSummary(
      accountId,
      moment()
        .subtract(1, 'month')
        .startOf('day')
        .valueOf(),
      moment()
        .endOf('day')
        .valueOf(),
      false
    ).then(data => {
      const topPageVisits = data?.pageViewCounts
      const topTopics = data?.topics
      setAccountSiteVisitInfo(topPageVisits)
      setAccountTopTopics(topTopics)
    })
  }, [accountId])

  return (
    <Box
      css={{
        marginBottom: '$2'
      }}>
      <Card css={{ padding: '18px 0px 0px 0px' }}>
        <Card.Header as={Flex} gap="half" alignItems="center" css={{ padding: '0px 10px' }}>
          <HeaderIcon
            icon={faEye}
            size="large"
            css={{
              backgroundColor: '$green6'
            }}
          />
          <Header variant="h4">TOP PAGES</Header>
        </Card.Header>
        <Card.Content>
          <Table
            rows={accountSiteVisitInfo as AccountSiteVisitInfo[]}
            columns={tableColumns}
            disableBoxShadow={true}
            emptyState={{
              title: 'No data available',
              subtitle: '',
              minHeight: 200
            }}
          />
        </Card.Content>
      </Card>
      {accountTopTopics?.length ? (
        <Card css={{ padding: '$2', marginTop: '$2' }}>
          <Card.Header as={Flex} gap="half" alignItems="center">
            <Flex gap={0} alignItems="center" justifyContent="space-between" fullWidth>
              <Flex gap={0} alignItems="center">
                <HeaderIcon
                  icon={faMagnifyingGlass}
                  size="large"
                  css={{
                    marginRight: '4px',
                    backgroundColor: '$blue8'
                  }}
                />
                <Header variant="h4">UTM TERMS</Header>
              </Flex>
              <Text variant="metadata"> {getTopicMessage(accountTopTopics.length)} </Text>
            </Flex>
          </Card.Header>
          <Card.Content>
            <Flex wrap="wrap">
              {accountTopTopics.map((topic, index) => {
                return (
                  <Tag key={`${topic}`} variant={getRandomVariant(index)}>
                    {topic}
                  </Tag>
                )
              })}
            </Flex>
          </Card.Content>
        </Card>
      ) : null}
    </Box>
  )
}

export default AccountSiteVisitInfoTable
